import { useMemo } from "react";
import { sameId } from "../model/inp/idLookup.ts";

// Shared state management for the "named collection editor" shape used by
// several ProjectDataDialog.tsx tabs (curves, patterns, pollutants,
// aquifers, ...). Deliberately only state, not JSX/rendering — each editor
// has a genuinely different detail body, so a single generic rendering
// component would either lose per-editor behavior or need render-prop
// escape hatches. Each editor still owns its own name-generation
// (`nextAvailableXxxName` + default-object shape).

export function useNamedCollection<T extends { name: string }>(
  items: T[],
  onChange: (items: T[]) => void,
  selectedName: string | null,
  onSelect: (name: string | null) => void
) {
  const selected = useMemo(
    () => items.find((i) => i.name === selectedName) ?? null,
    [items, selectedName]
  );

  /** Appends `item` (its own name already generated by the caller, e.g. `nextAvailableCurveName`) and selects it. */
  function add(item: T) {
    onChange([...items, item]);
    onSelect(item.name);
  }

  function remove(name: string) {
    onChange(items.filter((i) => i.name !== name));
    if (selectedName === name) onSelect(null);
  }

  /** Renaming to a blank or already-used name is silently ignored rather than saved — the only guard against two items merging into one on Save. The duplicate check is case-insensitive (`sameId`) since real SWMM ids are matched that way. */
  function rename(name: string) {
    if (!selected) return;
    const trimmed = name.trim();
    if (!trimmed || (!sameId(trimmed, selected.name) && items.some((i) => sameId(i.name, trimmed))))
      return;
    onChange(items.map((i) => (i.name === selected.name ? { ...i, name: trimmed } : i)));
    onSelect(trimmed);
  }

  function update(fn: (item: T) => T) {
    if (!selected) return;
    onChange(items.map((i) => (i.name === selected.name ? fn(i) : i)));
  }

  return { selected, add, remove, rename, update };
}
